import { ContentChildren, Directive, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, QueryList } from '@angular/core';
import {
  attachClosestEdge
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Panel } from 'primeng/panel';
import { NxDragItemDirective } from './nx-drag-item.directive';
import { Subject, takeUntil, timer } from 'rxjs';

@Directive({
  selector: '[nxDropList]'
})
export class NxDropListDirective<T = any> implements OnInit, OnDestroy {

  @Input() nxDropListData: T;
  @Input() nxIsDropDisabled: boolean = false;
  @Input() nxPanel: Panel;
  @Input() nxPanelExpandDelay: number = 1000; 
  @Output() nxDropped = new EventEmitter<any>();
  @Output() nxDropListEntered = new EventEmitter<any>();
  @Output() nxDropListLeft = new EventEmitter<any>();

  private cleanup: (() => void) | null = null;
  private destroy$ = new Subject<void>();
  private expandTimer$ = new Subject<void>();

  @ContentChildren(forwardRef(() => NxDragItemDirective)) nxDragItems: QueryList<NxDragItemDirective<T>>;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngOnInit() {
    let self = this;
    const el = this.elementRef.nativeElement;
    this.cleanup = dropTargetForElements({
      element: this.elementRef.nativeElement,
      canDrop: () => !this.nxIsDropDisabled,
      getData: ({ input }) => {
        let data = {
          data: this.nxDropListData,
          sourceContainer: self
        }
        return attachClosestEdge(data, {
          element : this.elementRef.nativeElement,
          input,
          allowedEdges: ['top', 'bottom'],
        });
      },
      onDragStart: args => {
      },
      onDrag: args => {},
      onDragEnter: args => {
        if(this.nxPanel && this.nxPanel.collapsed){
          // Start timer for panel expansion
          timer(this.nxPanelExpandDelay).pipe(
            takeUntil(this.expandTimer$),
            takeUntil(this.destroy$)
          ).subscribe(() => {
            // Double-check panel is still collapsed and we're still in drag state
            if(this.nxPanel && this.nxPanel.collapsed) {
              this.nxPanel.animating = true;
              this.nxPanel.expand();
            }
          });
        }
        // console.log('onDragEnter', args);
        // console.log(this.nxDragItems);
        this.nxDropListEntered.emit(args);
        this.elementRef.nativeElement.classList.add('dropActive');
      },
      onDragLeave: args => {
        // Cancel any pending panel expansion
        this.expandTimer$.next();

        this.nxDropListLeft.emit(args);
        this.elementRef.nativeElement.classList.remove('dropActive');
      },
      onDrop: args => {
        // Cancel any pending panel expansion
        this.expandTimer$.next();

        this.elementRef.nativeElement.classList.remove('dropActive');
        // only fire if the drop target is the first in the bubbled up list
        // if not, a it was dropped on a child element
        if(args.location.current.dropTargets[0]?.element === this.elementRef.nativeElement){
          let previousIndex = args.source?.data?.index !== undefined ? args.source?.data?.index as number : undefined;
          let previousContainer = args.source?.data?.container ? args.source?.data?.container as NxDropListDirective : undefined;
          let currentIndex = undefined; // just dropped IN the list, not an a specific index
          let container = this;
          let item = args.source?.data?.data;
          
          let event = {
            previousIndex: previousIndex,
            previousContainer: previousContainer,
            newIndex: currentIndex,
            newContainer: container,
            item: item,
          }
          // console.log(event);
          this.nxDropped.emit(event);
          
          this.elementRef.nativeElement.classList.remove('drop-active');  
          return;
        } else {
          return;
        }
        
      },

    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.expandTimer$.complete();
    if (this.cleanup) {
      this.cleanup();
    }
  }
}